import React, { useEffect, useState, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { apiFetch } from "../api/client";
import { COLORS, RADIUS } from "../theme";
import Card from "../components/ui/Card";
import Btn from "../components/ui/Btn";
import Input from "../components/ui/Input";
import Modal from "../components/ui/Modal";
import EstadoCarregando from "../components/ui/EstadoCarregando";
import EstadoVazio from "../components/ui/EstadoVazio";
import { ThFiltroTexto, ThSimples, BarraResumoFiltro } from "../components/ui/ThFiltro";
import { capitalizarPrimeira } from "../utils/texto";

function dataLocal(data) {
  return capitalizarPrimeira(new Date(data + "T12:00").toLocaleDateString("pt-BR", { weekday: "long", day: "2-digit", month: "long", year: "numeric" }));
}

// Dia da semana + dia do mês, sem o ano (usado na lista de sugestões de
// importação) — só o dia da semana sozinho não deixava claro qual data exata
// cada feriado móvel (Carnaval, Corpus Christi etc.) cai naquele ano.
function diaSemanaEDia(data) {
  const d = new Date(data + "T12:00");
  const diaSemana = capitalizarPrimeira(d.toLocaleDateString("pt-BR", { weekday: "long" }));
  const diaMes = d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
  return `${diaSemana}, ${diaMes}`;
}

const VAZIO = { data: "", nome: "" };

export default function Feriados() {
  const { token } = useAuth();
  const [feriados, setFeriados] = useState([]);
  const [loading, setLoading] = useState(true);

  const [modalNovo, setModalNovo] = useState(false);
  const [form, setForm] = useState(VAZIO);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState("");

  const [modalImportar, setModalImportar] = useState(false);
  const [anoImportar, setAnoImportar] = useState(new Date().getFullYear());
  const [sugestoes, setSugestoes] = useState([]);
  const [selecionados, setSelecionados] = useState([]);
  const [carregandoSugestoes, setCarregandoSugestoes] = useState(false);
  const [importando, setImportando] = useState(false);

  const [filtroData, setFiltroData] = useState("");
  const [filtroNome, setFiltroNome] = useState("");

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiFetch("GET", "/api/feriados", undefined, token);
      setFeriados(data);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { carregar(); }, [carregar]);

  const abrirNovo = () => {
    setForm(VAZIO);
    setErro("");
    setModalNovo(true);
  };

  const salvar = async () => {
    setErro("");
    if (!form.data || !form.nome.trim()) {
      setErro("Preencha a data e o nome do feriado.");
      return;
    }
    setSalvando(true);
    try {
      await apiFetch("POST", "/api/feriados", { data: form.data, nome: form.nome.trim() }, token);
      setModalNovo(false);
      await carregar();
    } catch (e) {
      setErro(e.message);
    } finally {
      setSalvando(false);
    }
  };

  const excluir = async (feriado) => {
    if (!window.confirm(`Remover "${feriado.nome}" (${dataLocal(feriado.data)})?`)) return;
    try {
      await apiFetch("DELETE", `/api/feriados/${feriado.id}`, undefined, token);
      await carregar();
    } catch (e) {
      window.alert(e.message);
    }
  };

  const abrirImportar = async () => {
    setModalImportar(true);
    setCarregandoSugestoes(true);
    try {
      const data = await apiFetch("GET", `/api/feriados/sugestoes/${anoImportar}`, undefined, token);
      const datasExistentes = new Set(feriados.map((f) => f.data));
      setSugestoes(data);
      setSelecionados(data.filter((s) => !datasExistentes.has(s.data)).map((s) => s.data));
    } finally {
      setCarregandoSugestoes(false);
    }
  };

  const alternarSugestao = (data) => {
    setSelecionados((atual) => (atual.includes(data) ? atual.filter((d) => d !== data) : [...atual, data]));
  };

  const importarSelecionados = async () => {
    setImportando(true);
    try {
      for (const s of sugestoes.filter((x) => selecionados.includes(x.data))) {
        try {
          await apiFetch("POST", "/api/feriados", { data: s.data, nome: s.nome }, token);
        } catch (e) {
          // já existe nessa data (409) — ignora e segue pros outros
        }
      }
      setModalImportar(false);
      await carregar();
    } finally {
      setImportando(false);
    }
  };

  const datasExistentes = new Set(feriados.map((f) => f.data));

  const feriadosFiltrados = feriados.filter((f) => {
    if (filtroData && f.data !== filtroData) return false;
    if (filtroNome && !f.nome.toLowerCase().includes(filtroNome.toLowerCase())) return false;
    return true;
  });
  const filtrosAtivos = Boolean(filtroData || filtroNome);
  const limparFiltros = () => { setFiltroData(""); setFiltroNome(""); };

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24, flexWrap: "wrap", gap: 12 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 800, color: COLORS.text, margin: "0 0 4px", letterSpacing: "-0.02em" }}>
            Feriados
          </h1>
          <p style={{ fontSize: 13.5, color: COLORS.textMid, margin: 0 }}>
            Cadastre dias que não contam como falta nos relatórios, mesmo em escala normal.
            (Funcionários com escala 12x36 trabalham independente de feriados.)
          </p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <Btn variant="secondary" onClick={() => { setAnoImportar(new Date().getFullYear()); abrirImportar(); }}>
            Importar feriados nacionais
          </Btn>
          <Btn onClick={abrirNovo}>+ Novo feriado</Btn>
        </div>
      </div>

      {feriados.length > 0 && (
        <BarraResumoFiltro
          total={feriadosFiltrados.length}
          totalGeral={feriados.length}
          unidade="feriado(s)"
          filtrosAtivos={filtrosAtivos}
          onLimpar={limparFiltros}
        />
      )}

      <Card style={{ padding: 0 }}>
        {loading ? (
          <EstadoCarregando />
        ) : feriados.length === 0 ? (
          <EstadoVazio icon="calendar" texto={'Nenhum feriado cadastrado ainda. Use "Importar feriados nacionais" pra começar rápido.'} />
        ) : feriadosFiltrados.length === 0 ? (
          <EstadoVazio icon="calendar" texto="Nenhum feriado encontrado com esse filtro." />
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                <ThFiltroTexto label="Data" type="date" value={filtroData} onChange={setFiltroData} minWidth={140} />
                <ThFiltroTexto label="Nome" value={filtroNome} onChange={setFiltroNome} placeholder="Buscar..." minWidth={160} />
                <ThSimples label="" alinhamento="right" />
              </tr>
            </thead>
            <tbody>
              {feriadosFiltrados.map((f) => (
                <tr key={f.id} style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                  <td style={{ padding: "14px 20px", fontSize: 13.5, color: COLORS.text, fontWeight: 700 }}>{dataLocal(f.data)}</td>
                  <td style={{ padding: "14px 20px", fontSize: 13.5, color: COLORS.textMid }}>{f.nome}</td>
                  <td style={{ padding: "14px 20px", textAlign: "right" }}>
                    <button
                      onClick={() => excluir(f)}
                      style={{ background: "transparent", border: `1.5px solid ${COLORS.border}`, borderRadius: 8, padding: "6px 12px", fontSize: 12.5, fontWeight: 600, color: COLORS.danger, cursor: "pointer" }}
                    >
                      Excluir
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Modal open={modalNovo} onClose={() => setModalNovo(false)} title="Novo feriado" width={380}>
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <Input label="Data" type="date" value={form.data} onChange={(e) => setForm({ ...form, data: e.target.value })} />
          <Input label="Nome" value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} placeholder="Ex: Aniversário da cidade" />
          {erro && (
            <div style={{ color: COLORS.danger, fontSize: 13, background: COLORS.dangerBg, border: "1px solid #FECACA", borderRadius: 8, padding: "9px 13px" }}>
              {erro}
            </div>
          )}
          <Btn onClick={salvar} disabled={salvando}>{salvando ? "Salvando..." : "Cadastrar"}</Btn>
        </div>
      </Modal>

      <Modal open={modalImportar} onClose={() => setModalImportar(false)} title="Importar feriados nacionais" width={440}>
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div style={{ display: "flex", alignItems: "flex-end", gap: 10 }}>
            <div style={{ flex: 1 }}>
              <Input
                label="Ano"
                type="number"
                value={anoImportar}
                onChange={(e) => setAnoImportar(Number(e.target.value))}
              />
            </div>
            <Btn variant="secondary" onClick={abrirImportar} disabled={carregandoSugestoes}>
              {carregandoSugestoes ? "Buscando..." : "Buscar"}
            </Btn>
          </div>

          <p style={{ fontSize: 12, color: COLORS.textLight, margin: 0 }}>
            Inclui os feriados nacionais fixos e móveis (Carnaval, Sexta-feira Santa, Corpus Christi, calculados a partir da Páscoa daquele ano). Desmarque o que não se aplicar à sua empresa.
          </p>

          {carregandoSugestoes ? (
            <EstadoCarregando padding={0} />
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 2, maxHeight: 320, overflowY: "auto" }}>
              {sugestoes.map((s) => {
                const jaExiste = datasExistentes.has(s.data);
                return (
                  <label
                    key={s.data}
                    style={{
                      display: "flex", alignItems: "center", gap: 10, padding: "8px 6px", borderRadius: RADIUS.sm,
                      cursor: jaExiste ? "default" : "pointer", opacity: jaExiste ? 0.5 : 1,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={selecionados.includes(s.data)}
                      disabled={jaExiste}
                      onChange={() => alternarSugestao(s.data)}
                      style={{ width: 16, height: 16 }}
                    />
                    <span style={{ fontSize: 13, color: COLORS.text, flex: 1 }}>{s.nome}</span>
                    <span style={{ fontSize: 12, color: COLORS.textLight }}>
                      {jaExiste ? "já cadastrado" : diaSemanaEDia(s.data)}
                    </span>
                  </label>
                );
              })}
            </div>
          )}

          <Btn onClick={importarSelecionados} disabled={importando || selecionados.length === 0}>
            {importando ? "Importando..." : `Importar ${selecionados.length} selecionado(s)`}
          </Btn>
        </div>
      </Modal>
    </div>
  );
}
